<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Mobile Sidebar Overlay -->
    <div 
      v-if="sidebarOpen" 
      class="fixed inset-0 z-40 lg:hidden"
      @click="sidebarOpen = false"
    >
      <div class="absolute inset-0 bg-black bg-opacity-50"></div>
    </div>

    <!-- Sidebar -->
    <div 
      class="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col"
      :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <!-- Sidebar Header with Logo -->
      <div class="flex items-center justify-between h-20 px-6 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center">
          <div class="w-16 h-16 mr-4">
            <img 
              src="/logo6.png" 
              alt="Annzz Logo" 
              class="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 class="text-xl font-bold text-gray-900">ANNZZ</h1>
            <p class="text-xs text-gray-500">Workshop Portal</p>
          </div>
        </div>
        
        <!-- Mobile Menu Button -->
        <button 
          @click="sidebarOpen = false"
          class="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- User Info (no profile page: workshop logins are managed by the admin) -->
      <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div class="w-full flex flex-col items-center p-4 rounded-lg border border-gray-200">
          <UserDisplay />
        </div>
      </div>

      <!-- Contact Us Button -->
      <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <button
          @click="showContactModal = true"
          class="w-full flex items-center justify-center px-4 py-3 rounded-lg text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 border border-gray-200 hover:border-orange-300 hover:shadow-sm"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Contact Us
        </button>
      </div>

      <!-- Sidebar Navigation -->
      <nav class="flex flex-col flex-1 overflow-y-auto">
        <!-- Main Navigation Items -->
        <div class="flex-1 px-4 pt-8">
          <div class="space-y-2">
            <!-- Jobs -->
            <router-link 
              to="/workshop/jobs" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/workshop/jobs' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Jobs
            </router-link>
          </div>
        </div>

        <!-- Bottom Section with Logout -->
        <div class="px-4 py-6 flex-shrink-0">
          <!-- Logout Button -->
          <button
            @click="logout"
            class="w-full group flex items-center px-4 py-3 rounded-lg text-sm font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-200"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      </nav>
    </div>

    <!-- Contact Us Modal -->
    <ContactUsModal 
      :show="showContactModal" 
      @close="showContactModal = false" 
    />

    <!-- Main Content -->
    <div class="lg:pl-64">
      <!-- Mobile Header -->
      <div class="lg:hidden bg-white shadow-sm border-b border-gray-200">
        <div class="flex items-center justify-between h-16 px-4">
          <button
            @click="sidebarOpen = true"
            class="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 class="text-lg font-semibold text-gray-900">Workshop Portal</h1>
          <div class="w-8"></div> <!-- Spacer for centering -->
        </div>
      </div>

      <!-- Main Content Area -->
      <main class="min-h-screen bg-gray-50">
        <div class="py-6 px-4 sm:px-6 lg:px-8">
          <router-view />
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useUserStore } from '../../stores/user'
import { useRouter } from 'vue-router'
import UserDisplay from '../../components/UserDisplay.vue'
import ContactUsModal from '../../components/ContactUsModal.vue'

export default {
  name: 'WorkshopLayout',
  components: {
    UserDisplay,
    ContactUsModal
  },
  setup() {
    const userStore = useUserStore()
    const router = useRouter()
    const sidebarOpen = ref(false)
    const showContactModal = ref(false)

    const logout = async () => {
      await userStore.signOut()
      router.push('/')
    }

    return {
      userStore,
      sidebarOpen,
      showContactModal,
      logout,
      router
    }
  }
}
</script> 